import { turnedOff } from '@/containers/sequences/sender-connections/sender-status.util';
import type { EngageMailbox } from '@/store/features/engage.feature';
import type { SendLinkedInActionConfig } from '@/types/custom-playbooks';

/**
 * The step "Send on LinkedIn": one LinkedIn message or invitation a person
 * from a member's own LinkedIn account. Mirrors cleanse-api
 * `utils/play_linkedin_action.py`; the server's validator, save check (only
 * the account's owner may pick it) and send checks are the authority.
 */

export type LinkedInAction = SendLinkedInActionConfig['action'];

export const LINKEDIN_ACTION_OPTIONS: {
  value: LinkedInAction;
  label: string;
}[] = [
  { value: 'message', label: 'Message' },
  { value: 'invite', label: 'Invite' },
];

/** What the step does, by action (the API's ACTION_WORDS). */
export const LINKEDIN_ACTION_WORDS: Record<LinkedInAction, string> = {
  message: 'Send a message',
  invite: 'Send an invitation',
};

export const LINKEDIN_STEP_LABEL = 'Send on LinkedIn';
export const LINKEDIN_MESSAGE_MAX = 8_000;
export const LINKEDIN_NOTE_MAX = 200;

/** The code a person the step left out carries, with the reason. */
export const LINKEDIN_ACTION_SKIP_CODE = 'linkedin_action_skipped';

const ACCOUNT_MISSING = 'Pick a LinkedIn account';

export function linkedInActionOf(config: unknown): LinkedInAction | null {
  const action = (config as Partial<SendLinkedInActionConfig> | undefined)
    ?.action;
  return action === 'message' || action === 'invite' ? action : null;
}

/** The name a picked account is saved with, as the server stamps it. */
export function linkedInAccountName(account: EngageMailbox): string {
  return account.display_name || account.email_address || 'LinkedIn account';
}

/**
 * The accounts the member may pick: their own LinkedIn accounts, connected
 * and turned on. Never a teammate's, shared or not: the server refuses any
 * other.
 */
export function ownLinkedInAccounts(
  accounts: readonly EngageMailbox[],
  userId: string | null | undefined
): EngageMailbox[] {
  if (!userId) return [];
  return accounts.filter(
    (account) =>
      account.owner_user_id === userId &&
      account.provider === 'linkedin' &&
      account.connected &&
      account.active
  );
}

/** The state beside a saved account the member can't pick, or null when it
 *  is one of theirs and ready. */
export function savedLinkedInAccountState(
  account: EngageMailbox | undefined,
  userId: string | null | undefined
): string | null {
  if (!account || account.provider !== 'linkedin') return 'Not found';
  if (account.owner_user_id !== userId) return 'Teammate’s account';
  if (!account.connected) return turnedOff(account)?.label ?? 'Disconnected';
  if (!account.active) return 'Off';
  return null;
}

/** Why a Send on LinkedIn step can't run yet, or null. */
export function sendLinkedInActionIssue(
  config: Partial<SendLinkedInActionConfig> | undefined
): string | null {
  const action = linkedInActionOf(config);
  const text = typeof config?.text === 'string' ? config.text : '';
  if (!config?.accountId) return ACCOUNT_MISSING;
  if (!action) return 'Choose Message or Invite';
  if (action === 'message' && !text.trim()) return 'Write the message';
  if (action === 'message' && text.length > LINKEDIN_MESSAGE_MAX)
    return 'Message limit: 8,000 characters';
  if (action === 'invite' && text.length > LINKEDIN_NOTE_MAX)
    return `Invitation note limit: ${LINKEDIN_NOTE_MAX} characters`;
  return null;
}

/** "Message from Dana Smith", as the API's step_summary. */
export function sendLinkedInActionSummary(
  config: Partial<SendLinkedInActionConfig> | undefined
): string {
  const sender = config?.accountId ? config.accountName?.trim() : '';
  if (!sender) return ACCOUNT_MISSING;
  const action = linkedInActionOf(config);
  if (!action) return `From ${sender}`;
  return `${action === 'invite' ? 'Invitation' : 'Message'} from ${sender}`;
}

/** What follows the tool on the card's headline: the account once one is
 *  picked, else what the step does (the API's headline_words). */
export function sendLinkedInActionHeadlineWords(
  config: Partial<SendLinkedInActionConfig> | undefined
): string {
  const action = linkedInActionOf(config);
  return (
    config?.accountName?.trim() ||
    (action ? LINKEDIN_ACTION_WORDS[action] : LINKEDIN_STEP_LABEL)
  );
}
